<script setup lang="ts">
import FinestCard from '@/components/FinestCard.vue'
import PropertyStepErrors from '@/components/portfolio/PropertyStepErrors.vue'
import type { ExtendedEnum } from '@/types'
import { useI18n } from 'vue-i18n'
import EstateObjectType = App.Enums.EstateObject.EstateObjectType

defineProps<{
  types: (ExtendedEnum<EstateObjectType> & { has_living_space: boolean })[]
  errors: string[]
}>()

const selected = defineModel<EstateObjectType | null>({ default: null })

/** Ein Klick ist die Antwort – wohin es danach geht, entscheidet der Ablauf drumherum. */
const emit = defineEmits<{
  select: [type: EstateObjectType]
}>()

function choose(type: EstateObjectType): void {
  selected.value = type
  emit('select', type)
}

const { t } = useI18n({ useScope: 'local' })

const typeIcons: Record<string, string> = {
  apartment_building: 'pi pi-building',
  two_party_house: 'pi pi-building-columns',
  single_family_house: 'pi pi-home',
  condominium: 'pi pi-key',
  office: 'pi pi-briefcase',
  commercial: 'pi pi-shop',
  care_and_health: 'pi pi-heart',
  land: 'pi pi-map',
  hotel: 'pi pi-star',
  logistics: 'pi pi-truck',
  wholesale_and_retail: 'pi pi-shopping-cart',
  other: 'pi pi-ellipsis-h',
}
</script>

<template>
  <FinestCard :title="t('title')" icon="pi pi-tag">
    <p class="text-ink-2 mb-4 text-sm">{{ t('hint') }}</p>

    <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      <button
        v-for="option in types"
        :key="option.id"
        type="button"
        class="focus-visible:ring-action/35 flex cursor-pointer flex-col items-center gap-2 rounded-lg border p-4 text-center transition-colors focus-visible:ring-[3px] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        :class="
          selected === option.id
            ? 'border-action bg-info-subtle text-ink'
            : 'border-line hover:border-action hover:bg-info-subtle text-ink-2'
        "
        :disabled="option.disabled"
        :aria-pressed="selected === option.id"
        @click="choose(option.id)">
        <i :class="typeIcons[option.id] ?? 'pi pi-building'" class="text-2xl!" aria-hidden="true" />
        <span class="text-sm font-medium">{{ t(`enums.estateObjectType.${option.id}`) }}</span>
      </button>
    </div>

    <PropertyStepErrors :messages="errors" class="mt-4" />
  </FinestCard>
</template>

<i18n lang="json">
{
  "de": {
    "title": "Objektart",
    "hint": "Die Objektart bestimmt, welche Angaben danach relevant sind. Ein Klick genügt."
  },
  "en": {
    "title": "Property type",
    "hint": "The property type decides which details matter afterwards. One click is enough."
  }
}
</i18n>
